import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface AccountGoalsProps {
  report?: any;
  accountReport?: any;
  data?: any;
  accountInfo?: any;
  maxLoss?: number | string | null;
  dailyLoss?: number | string | null;
  targetProfit?: number | string | null;
  currentMaxLoss?: number | string | null;
  currentDailyLoss?: number | string | null;
  currentProfit?: number | string | null;
  /** Analysis tab: compute from closed trades of the filtered period only (ignore live equity / ftmo_progress) */
  closedTradesOnly?: boolean;
}

interface GoalColorConfig {
  barBg: string;
  textColor: string;
}

interface GoalItem {
  label: string;
  targetPercent: number;
  goal: string;
  current: string;
  percent: number;
  rawPercent?: number;
  colorConfig: GoalColorConfig;
  isTarget?: boolean;
  /** shown on the right of the label instead of `${targetPercent}%` (e.g. "4" trading days) */
  headerValue?: string;
  status?: 'passed' | 'failed' | 'ongoing';
}


const AccountGoals: React.FC<AccountGoalsProps> = ({
  report,
  accountReport,
  data,
  accountInfo,
  maxLoss: propMaxLoss,
  dailyLoss: propDailyLoss,
  targetProfit: propTargetProfit,
  currentMaxLoss: propCurrentMaxLoss,
  currentDailyLoss: propCurrentDailyLoss,
  currentProfit: propCurrentProfit,
  closedTradesOnly = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const parseNum = (val: any): number => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return isNaN(val) ? 0 : val;
    if (typeof val === 'string') {
      const cleaned = val.replace(/[^0-9.-]/g, '');
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

  const formatAmount = (val?: number | null): string => {
    const num = parseNum(val);
    if (num === 0) return '$0';
    const isNegative = num < 0;
    const absNum = Math.abs(num);
    const formatted = absNum.toLocaleString('en-US', {
      minimumFractionDigits: absNum % 1 !== 0 ? 2 : 0,
      maximumFractionDigits: 2,
    });
    return isNegative ? `-$${formatted}` : `$${formatted}`;
  };

  // 1. Trích xuất đa năng dữ liệu từ report / accountReport / data / accountInfo
  const repData = (
    report?.data?.data ||
    report?.data ||
    accountReport?.data?.data ||
    accountReport?.data ||
    data?.data ||
    data ||
    report ||
    accountReport ||
    {}
  );

  const accInfo = (
    accountInfo ||
    repData?.account_info ||
    report?.account_info ||
    report?.data?.account_info ||
    report?.data?.data?.account_info ||
    accountReport?.account_info ||
    accountReport?.data?.account_info ||
    accountReport?.data?.data?.account_info ||
    {}
  );

  // Hỗ trợ cả journal_closed_trades lẫn raw_trades và trades
  const rawTrades: any[] = (
    (Array.isArray(repData?.journal_closed_trades) && repData.journal_closed_trades.length > 0 ? repData.journal_closed_trades : null) ||
    (Array.isArray(repData?.raw_trades) && repData.raw_trades.length > 0 ? repData.raw_trades : null) ||
    (Array.isArray(repData?.closed_trades) && repData.closed_trades.length > 0 ? repData.closed_trades : null) ||
    (Array.isArray(repData?.trades) && repData.trades.length > 0 ? repData.trades : null) ||
    (Array.isArray(report?.journal_closed_trades) && report.journal_closed_trades.length > 0 ? report.journal_closed_trades : null) ||
    (Array.isArray(report?.raw_trades) && report.raw_trades.length > 0 ? report.raw_trades : null) ||
    (Array.isArray(report?.data?.journal_closed_trades) && report.data.journal_closed_trades.length > 0 ? report.data.journal_closed_trades : null) ||
    (Array.isArray(report?.data?.raw_trades) && report.data.raw_trades.length > 0 ? report.data.raw_trades : null) ||
    (Array.isArray(accountReport?.journal_closed_trades) && accountReport.journal_closed_trades.length > 0 ? accountReport.journal_closed_trades : null) ||
    (Array.isArray(accountReport?.raw_trades) && accountReport.raw_trades.length > 0 ? accountReport.raw_trades : null) ||
    (Array.isArray(accountReport?.data?.journal_closed_trades) && accountReport.data.journal_closed_trades.length > 0 ? accountReport.data.journal_closed_trades : null) ||
    (Array.isArray(accountReport?.data?.raw_trades) && accountReport.data.raw_trades.length > 0 ? accountReport.data.raw_trades : null) ||
    []
  );

  const initialBalance = parseNum(
    accInfo?.initial_balance ??
    repData?.initial_balance ??
    report?.initial_balance ??
    repData?.balance_init ??
    repData?.performance_summary?.initial_balance ??
    repData?.equity_analysis?.equity_curve?.[0]?.balance ??
    (propMaxLoss ? Number(propMaxLoss) * 10 : 0)
  );

  const currentBalance = parseNum(
    accInfo?.balance ??
    repData?.today_balance ??
    repData?.balance ??
    report?.today_balance ??
    report?.balance ??
    repData?.performance_summary?.current_balance ??
    repData?.current_equity ??
    repData?.performance_summary?.current_equity ??
    0
  );

  const accountStatus = String(
    accInfo?.account_status ||
    accInfo?.type ||
    repData?.account_status ||
    repData?.type ||
    report?.account_status ||
    ''
  ).toLowerCase().trim();

  const hasData = initialBalance > 0 || currentBalance > 0 || (propMaxLoss != null && Number(propMaxLoss) > 0);

  // 2. Mức lỗ tối đa (Max Loss): 10% tổng tài khoản (initial_balance)
  // Lấy balance hiện tại trừ initial_balance
  const maxLossTargetPct = 10;
  let calculatedMaxLoss = initialBalance * 0.1;
  let calculatedCurrentMaxLoss = 0;
  if (hasData) {
    const diff = currentBalance - initialBalance;
    // Nếu balance < initial_balance: đang bị lỗ diff < 0
    calculatedCurrentMaxLoss = diff < 0 ? Math.abs(diff) : 0;
  }

  // 3. Mức lỗ ngày tối đa (Daily Loss): 5% tổng tài khoản (initial_balance)
  // Tổng các lệnh lỗ hôm nay
  const dailyLossTargetPct = 5;
  let calculatedDailyLoss = initialBalance * 0.05;
  let calculatedCurrentDailyLoss = 0;
  if (hasData) {
    // Xác định ngày của báo cáo để tìm trade hôm nay
    const reportDateStr = (
      (Array.isArray(repData?.daily_summary) && repData.daily_summary.length > 0
        ? String(repData.daily_summary[repData.daily_summary.length - 1]?.date || '').trim()
        : '') ||
      (rawTrades.length > 0 ? String(rawTrades[rawTrades.length - 1]?.close_time || '').slice(0, 10) : '') ||
      String(repData?.last_updated_time || '').slice(0, 10) ||
      String(repData?.date_to || '').slice(0, 10) ||
      new Date().toISOString().slice(0, 10)
    );

    const now = new Date();
    const localToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const utcToday = now.toISOString().slice(0, 10);

    const todayTrades = rawTrades.filter((t: any) => {
      const d = String(t?.close_time || '').slice(0, 10);
      return d === reportDateStr || d === localToday || d === utcToday;
    });

    if (todayTrades.length > 0) {
      calculatedCurrentDailyLoss = todayTrades.reduce((sum: number, t: any) => {
        const net = parseNum(t?.net_profit ?? t?.profit_gross ?? t?.profit ?? 0);
        return net < 0 ? sum + Math.abs(net) : sum;
      }, 0);
    } else if (Array.isArray(repData?.daily_summary) && repData.daily_summary.length > 0) {
      const lastDay = repData.daily_summary[repData.daily_summary.length - 1];
      const dayResult = parseNum(lastDay?.result ?? lastDay?.net_profit ?? lastDay?.profit ?? 0);
      if (dayResult < 0) {
        calculatedCurrentDailyLoss = Math.abs(dayResult);
      }
    } else if (parseNum(repData?.net_profit_today) < 0) {
      calculatedCurrentDailyLoss = Math.abs(parseNum(repData.net_profit_today));
    }
  }

  // 4. Lợi nhuận mục tiêu (Profit Target):
  // account_status -> Challenge = 10%, Verification = 5%, còn lại mặc định 10%
  // Lấy balance hiện tại trừ initial_balance
  const isVerification = accountStatus.includes('verification');
  const profitTargetPct = isVerification ? 5 : 10;
  let calculatedTargetProfit = initialBalance * (profitTargetPct / 100);
  let calculatedCurrentProfit = 0;
  if (hasData) {
    calculatedCurrentProfit = currentBalance - initialBalance;
  }

  // Backend đã tính sẵn tiến độ theo equity (bao gồm lệnh đang mở) -> ưu tiên dùng.
  // Phần tự tính ở trên chỉ là fallback khi API chưa trả ftmo_progress.
  // Ở chế độ closedTradesOnly thì thay bằng số liệu chỉ từ lệnh đã đóng trong kỳ lọc.
  let progress = repData?.ftmo_progress;
  if (closedTradesOnly) {
    const closed = [...(Array.isArray(repData?.journal_closed_trades) ? repData.journal_closed_trades : [])]
      .sort((a: any, b: any) => new Date(a.close_time).getTime() - new Date(b.close_time).getTime());
    const balances = closed.map((tr: any) => parseNum(tr.balance_after)).filter((v: number) => v > 0);
    const closedBalance = balances.length ? balances[balances.length - 1] : initialBalance;
    // Lỗ tối đa: số dư (sau lệnh đóng) thấp nhất so với vốn ban đầu
    const lowestBalance = balances.length ? Math.min(...balances) : initialBalance;
    // Lỗ ngày: ngày có kết quả âm nhiều nhất trong kỳ (tổng ròng của ngày, không chỉ cộng lệnh thua)
    const dayResults = (Array.isArray(repData?.daily_summary) ? repData.daily_summary : []).map((d: any) => parseNum(d?.result));
    const worstDay = dayResults.length ? Math.min(0, ...dayResults) : 0;
    progress = {
      max_loss: { max_loss: initialBalance * 0.1, current_loss: Math.max(0, initialBalance - lowestBalance) },
      daily_loss: { max_loss: initialBalance * 0.05, current_loss: Math.abs(worstDay) },
      profit_target: { target_profit: calculatedTargetProfit, current_profit: closedBalance - initialBalance },
    };
  }
  const pick = (backendVal: any, fallback: number) =>
    backendVal !== undefined && backendVal !== null ? parseNum(backendVal) : fallback;

  // Fallback sang props truyền ngoài nếu chưa có dữ liệu tự tính
  const safeMaxLoss = pick(progress?.max_loss?.max_loss, hasData ? calculatedMaxLoss : parseNum(propMaxLoss));
  const safeCurrentMaxLoss = pick(progress?.max_loss?.current_loss, hasData ? calculatedCurrentMaxLoss : parseNum(propCurrentMaxLoss));

  // FTMO: Max Daily Loss amount = 5% of the INITIAL capital, applied to the balance at 00:00 CE(S)T.
  // The API sends 5% of today's balance (e.g. 10,018.96 instead of 10,000), so prefer our own amount.
  const safeDailyLoss = initialBalance > 0
    ? calculatedDailyLoss
    : pick(progress?.daily_loss?.max_loss, parseNum(propDailyLoss));
  const safeCurrentDailyLoss = pick(progress?.daily_loss?.current_loss, hasData ? calculatedCurrentDailyLoss : parseNum(propCurrentDailyLoss));

  const safeTargetProfit = pick(progress?.profit_target?.target_profit, hasData ? calculatedTargetProfit : parseNum(propTargetProfit));
  const safeCurrentProfit = pick(progress?.profit_target?.current_profit, hasData ? calculatedCurrentProfit : parseNum(propCurrentProfit));

  // Tiến độ thanh bar (%)
  const maxLossPercent = safeMaxLoss > 0
    ? Math.min(100, Math.max(0, (Math.abs(safeCurrentMaxLoss) / safeMaxLoss) * 100))
    : 0;

  const dailyLossPercent = safeDailyLoss > 0
    ? Math.min(100, Math.max(0, (Math.abs(safeCurrentDailyLoss) / safeDailyLoss) * 100))
    : 0;

  const profitPercent = safeTargetProfit > 0
    ? Math.min(100, Math.max(0, (safeCurrentProfit / safeTargetProfit) * 100))
    : 0;

  // Lỗ tối đa & Lỗ theo ngày: từ xanh lá (an toàn) sang đỏ (nguy hiểm)
  const getLossColorConfig = (current: number, max: number) => {
    if (max <= 0) return { barBg: 'bg-emerald-500', textColor: 'text-emerald-600' };
    const pct = (Math.abs(current) / max) * 100;
    if (pct < 50) return { barBg: 'bg-emerald-500', textColor: 'text-emerald-600' };
    if (pct < 80) return { barBg: 'bg-amber-400', textColor: 'text-amber-500' };
    return { barBg: 'bg-red-500', textColor: 'text-red-500' };
  };

  // Mục tiêu lợi nhuận: từ vàng (bắt đầu) sang xanh lá (đạt mục tiêu)
  const getProfitColorConfig = (current: number, target: number) => {
    if (target <= 0) return { barBg: 'bg-amber-400', textColor: 'text-amber-500' };
    const pct = (current / target) * 100;
    if (pct < 50) return { barBg: 'bg-amber-400', textColor: 'text-amber-500' };
    if (pct < 80) return { barBg: 'bg-emerald-400', textColor: 'text-emerald-500' };
    return { barBg: 'bg-emerald-500', textColor: 'text-emerald-600' };
  };

  const maxLossConfig = getLossColorConfig(safeCurrentMaxLoss, safeMaxLoss);
  const dailyLossConfig = getLossColorConfig(safeCurrentDailyLoss, safeDailyLoss);
  const profitConfig = getProfitColorConfig(safeCurrentProfit, safeTargetProfit);

  const goals: GoalItem[] = [
    {
      label: t('metrixAccountGoals.labels.maxLoss'),
      targetPercent: maxLossTargetPct,
      goal: formatAmount(safeMaxLoss),
      current: formatAmount(safeCurrentMaxLoss),
      percent: maxLossPercent,
      colorConfig: maxLossConfig,
      isTarget: false,
      status: safeMaxLoss > 0 && safeCurrentMaxLoss >= safeMaxLoss ? 'failed' : 'passed',
    },
    {
      label: t('metrixAccountGoals.labels.dailyLoss'),
      targetPercent: dailyLossTargetPct,
      goal: formatAmount(safeDailyLoss),
      current: formatAmount(safeCurrentDailyLoss),
      percent: dailyLossPercent,
      colorConfig: dailyLossConfig,
      isTarget: false,
      status: safeDailyLoss > 0 && safeCurrentDailyLoss >= safeDailyLoss ? 'failed' : 'passed',
    },
    {
      label: t('metrixAccountGoals.labels.profitTarget'),
      targetPercent: profitTargetPct,
      goal: formatAmount(safeTargetProfit),
      current: formatAmount(safeCurrentProfit),
      percent: profitPercent,
      rawPercent: safeTargetProfit > 0 ? (safeCurrentProfit / safeTargetProfit) * 100 : 0,
      colorConfig: profitConfig,
      isTarget: true,
      status: safeTargetProfit > 0 && safeCurrentProfit >= safeTargetProfit ? 'passed' : 'ongoing',
    },
  ];

  // FTMO 2-Step: at least 4 trading days in Challenge & Verification (not on the funded account).
  // FTMO counts days on which a position was opened; we only have days with closed trades.
  const MIN_TRADING_DAYS = 4;
  const tradingDays = parseNum(repData?.count_trading_days ?? (Array.isArray(repData?.daily_summary) ? repData.daily_summary.length : 0));
  if (hasData && !accountStatus.includes('funded')) {
    goals.push({
      label: t('metrixLive.goals.minTradingDays'),
      targetPercent: 0,
      headerValue: String(MIN_TRADING_DAYS),
      goal: t('metrixLive.goals.days', { count: MIN_TRADING_DAYS }),
      current: t('metrixLive.goals.days', { count: tradingDays }),
      percent: Math.min(100, (tradingDays / MIN_TRADING_DAYS) * 100),
      rawPercent: (tradingDays / MIN_TRADING_DAYS) * 100,
      colorConfig: tradingDays >= MIN_TRADING_DAYS
        ? { barBg: 'bg-emerald-500', textColor: 'text-emerald-600' }
        : { barBg: 'bg-amber-400', textColor: 'text-amber-500' },
      isTarget: true,
      status: tradingDays >= MIN_TRADING_DAYS ? 'passed' : 'ongoing',
    });
  }

  const statusStyle: Record<string, string> = {
    passed: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    failed: 'bg-red-50 text-red-600 border-red-200',
    ongoing: 'bg-slate-50 text-slate-500 border-slate-200',
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-premium border border-slate-100 p-3 sm:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div>
              <h2 className="!text-base font-bold !mb-0 !text-slate-900">
                {t('metrixAccountGoals.title')}
              </h2>
              {closedTradesOnly && (
                <p className="!text-xs !text-slate-500 !m-0 mt-1">{t('metrixLive.goalsClosedNote')}</p>
              )}
            </div>
            <div 
              onClick={() => setIsOpen(true)}
              className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-50"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
            </div>
          </div>
        </div>

        {/* Progress List */}
        <div className="space-y-7">
          {goals.map((g, idx) => (
            <div key={idx}>
              <div className="flex justify-between items-center mb-2">
                <span className="!text-sm !text-slate-600 font-medium flex items-center gap-2 min-w-0">
                  {g.label}
                  {hasData && g.status && (
                    <span className={`shrink-0 px-1.5 py-0.5 rounded border !text-[10px] font-bold uppercase ${statusStyle[g.status]}`}>
                      {t(`metrixLive.goals.status.${g.status}`)}
                    </span>
                  )}
                </span>
                <span className={`!text-sm font-bold font-mono-num ${g.colorConfig.textColor}`}>
                  {hasData ? (g.headerValue ?? `${g.targetPercent}%`) : '0%'}
                </span>
              </div>

              <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                <div
                  className={`${g.colorConfig.barBg} h-full rounded-full transition-all duration-1000`}
                  style={{ width: `${g.percent}%` }}
                ></div>
              </div>

              <div className="flex justify-between mt-2 !text-xs !text-slate-400 font-medium">
                <span>
                  {t('metrixAccountGoals.labels.current')}:{' '}
                  <span className={`font-bold ${g.colorConfig.textColor}`}>
                    {g.current}{' '}
                    {hasData
                      ? `(${((g.isTarget && g.rawPercent != null ? g.rawPercent : g.percent)).toFixed(1)}%)`
                      : ''}
                  </span>
                </span>
                <span>
                  {g.isTarget ? t('metrixAccountGoals.labels.target') : t('metrixAccountGoals.labels.limit')}: {g.goal}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Popup Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-[#0b1c2e]/45 flex items-center justify-center px-4 py-3 z-50 animate-fadeIn"
          onClick={(e) => e.target === e.currentTarget && setIsOpen(false)}
        >
          <div className="bg-white w-full max-w-[460px] rounded-2xl overflow-hidden shadow-2xl transition-all">
            {/* Pop Head */}
            <div className="bg-gradient-to-br from-[#0e2740] to-[#0b1c2e] text-white relative" style={{ padding: '1.25rem 1.5rem' }}>
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-3 right-3.5 w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs flex items-center justify-center transition-colors"
              >
                ✕
              </button>

              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center text-base">
                  🚩
                </div>
                <div>
                  <div className="text-sm font-bold">2-Step</div>
                  <div className="text-[11px] text-[#9fb0c4] font-medium">
                    {t('metrixAccountGoals.modal.subtitle')}
                  </div>
                </div>
              </div>

              <div className="mt-3 text-[11px] text-[#c7d3e0] flex items-center gap-1.5 font-semibold">
                <span className="text-white">{t('metrixAccountGoals.modal.steps.challenge')}</span>
                <span className="text-[#5c7186]">›</span>
                <span>{t('metrixAccountGoals.modal.steps.verification')}</span>
                <span className="text-[#5c7186]">›</span>
                <span>{t('metrixAccountGoals.modal.steps.account')}</span>
              </div>
            </div>

            {/* Pop Body */}
            <div style={{ padding: '1.25rem 1.5rem' }}>
              <div className="py-2.5 border-b border-slate-100">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500 font-medium">
                    {t('metrixAccountGoals.modal.profitTarget')}
                  </span>
                  <span className="font-bold text-[#0b1c2e]">10% / 5%</span>
                </div>
                <div className="mt-2 flex flex-col gap-1.5">
                  <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg p-2">
                    <div className="flex items-center gap-2">
                      <span className="w-4.5 h-4.5 rounded-full bg-[#0b1c2e] text-white text-[10px] font-bold flex items-center justify-center">
                        1
                      </span>
                      <span className="text-xs font-semibold text-slate-600">
                        {t('metrixAccountGoals.modal.steps.challenge')}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-[#0b1c2e]">10%</span>
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg p-2">
                    <div className="flex items-center gap-2">
                      <span className="w-4.5 h-4.5 rounded-full bg-teal-500 text-white text-[10px] font-bold flex items-center justify-center">
                        2
                      </span>
                      <span className="text-xs font-semibold text-slate-600">
                        {t('metrixAccountGoals.modal.steps.verification')}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-[#0b1c2e]">5%</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center py-2.5 border-b border-slate-100 text-xs">
                <span className="text-slate-500 font-medium">
                  {t('metrixAccountGoals.modal.maxLoss')}
                </span>
                <span className="font-bold text-[#0b1c2e]">10%</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-slate-100 text-xs">
                <span className="text-slate-500 font-medium">
                  {t('metrixAccountGoals.modal.maxDailyLoss')}
                </span>
                <span className="font-bold text-[#0b1c2e]">5%</span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-slate-100 text-xs">
                <span className="text-slate-500 font-medium">
                  {t('metrixAccountGoals.modal.minTradingDays')}
                </span>
                <span className="font-bold text-[#0b1c2e]">
                  {t('metrixAccountGoals.modal.days', { count: 4 })}
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5 border-b border-slate-100 text-xs">
                <span className="text-slate-500 font-medium">
                  {t('metrixAccountGoals.modal.tradingPeriod')}
                </span>
                <span className="font-bold text-[#0b1c2e]">
                  {t('metrixAccountGoals.modal.unlimited')}
                </span>
              </div>
              <div className="flex justify-between items-center py-2.5 text-xs">
                <span className="text-slate-500 font-medium">
                  {t('metrixAccountGoals.modal.rewards')}
                </span>
                <span className="font-bold text-[#0b1c2e]">
                  {t('metrixAccountGoals.modal.upTo', { percent: 90 })}
                </span>
              </div>
            </div>

            {/* Pop Foot */}
            <div className="bg-slate-50 p-3 px-5 text-[11.5px] text-slate-500 leading-relaxed">
              {t('metrixAccountGoals.modal.footerNote')}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountGoals;